import { ErrorBoundary } from '@sentry/react'
import React, { Suspense } from 'react'
import { Navigate } from 'react-router'

import CreateAccountModal from '@/components/layouts/ReferralInviteLayout/components/ClaimReferralModal'
import Header from '@/components/layouts/ReferralInviteLayout/components/Header'
import ReferralInviteAlreadyClaimedError from '@/components/layouts/ReferralInviteLayout/components/ReferralInviteClaimedError'
import { useReferralInvitesQuery } from '@/components/layouts/ReferralInviteLayout/hooks/useReferralInvitesQuery'
import FullPageErrorFallback from '@/components/misc/FullPageErrorFallback'
import LoadingWidget from '@/components/widgets/LoadingWidget'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/lib/routes'
import errorTypeService from '@/lib/services/errorTypeService'

interface Props {
  children: React.ReactNode
}

const ReferralInviteLayoutContent: React.FC<Props> = ({ children }) => {
  const { isAuthenticated, currentCompany, isClientUser } = useAuth()
  const shouldCompleteCompanySetup =
    isAuthenticated && !isClientUser && !currentCompany

  useReferralInvitesQuery()

  if (shouldCompleteCompanySetup) {
    return <Navigate to={ROUTES.addCompany()} replace />
  }

  return (
    <>
      <Header />
      {children}
      <CreateAccountModal />
    </>
  )
}

const ReferralInviteLayout: React.FC<Props> = ({ children }) => (
  <Suspense fallback={<LoadingWidget />}>
    <ErrorBoundary
      fallback={(error) => {
        const isPermissionError =
          errorTypeService.isForbiddenError(error.error) ||
          errorTypeService.isUnauthorizedError(error.error)

        return isPermissionError ? (
          <ReferralInviteAlreadyClaimedError />
        ) : (
          <FullPageErrorFallback error={error} />
        )
      }}
    >
      <ReferralInviteLayoutContent>{children}</ReferralInviteLayoutContent>
    </ErrorBoundary>
  </Suspense>
)

export default ReferralInviteLayout
